import { attachedFilesIn } from '../../components/ui/RichTextEditor/attachments';

// The upload rules in @eo/common hold the same limits; checking them here tells
// the user while attaching instead of when sending.
export const ATTACHMENT_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'heic',
  'pdf',
  'docx',
  'doc',
  'txt',
  'xlsx',
  'xls',
  'csv',
  'pptx',
  'ppt',
  'zip',
] as const;

export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

export const MAX_ATTACHMENT_MEGABYTES = 25;

export const MAX_ATTACHMENTS_TOTAL_MEGABYTES = 30;

const MEGABYTE = 1024 * 1024;

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

export function screenAttachments(
  files: File[],
  attached: File[],
): { accepted: File[]; refused: string[] } {
  const accepted: File[] = [];
  const refused: string[] = [];
  let total = attached.reduce((sum, f) => sum + f.size, 0);
  for (const file of files) {
    if (!(ATTACHMENT_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
      refused.push(
        `"${file.name}" can’t be attached. Attach images, PDF, Word, Excel, CSV, PowerPoint, text or ZIP files.`,
      );
    } else if (file.size > MAX_ATTACHMENT_MEGABYTES * MEGABYTE) {
      refused.push(`"${file.name}" is larger than ${MAX_ATTACHMENT_MEGABYTES} MB.`);
    } else if (total + file.size > MAX_ATTACHMENTS_TOTAL_MEGABYTES * MEGABYTE) {
      refused.push(
        `"${file.name}" would take these attachments past ${MAX_ATTACHMENTS_TOTAL_MEGABYTES} MB.`,
      );
    } else {
      accepted.push(file);
      total += file.size;
    }
  }
  return { accepted, refused };
}

// A body whose text points at files attached in the editor goes out as a form:
// the JSON in its `body` field names each file by its place among the files
// sent with it, which is how the server knows where to show it once uploaded.
export function withAttachments<T>(body: T): T | FormData {
  const json = JSON.stringify(body);
  const files = attachedFilesIn(json);
  if (files.length === 0) return body;
  const form = new FormData();
  form.append(
    'body',
    files.reduce((out, { url }, i) => out.replaceAll(url, `attachment:${i}`), json),
  );
  for (const { file } of files) form.append('files', file);
  return form;
}
